import { Row, RowSelectionState, Table } from '@tanstack/react-table';
import { ChartData, ScriptableContext } from 'chart.js';
import { round } from 'mathjs';
import { OrderEntryTableRow } from '../aggregated-order-table/columns';
import {
  CustomBarDataset,
  CustomLineDataset,
  CustomDoughnutDataset,
} from './types';
import { parseNumericValue } from './utils';
import { isGradient, createGradientFunction } from './gradientUtils';
import { ChakraColor, hexToRgb } from 'src/utils/color-util';
import { AggregatedOrderViewType } from 'src/gql/graphql';

// Define interface for border radius configuration
interface BorderRadiusConfig {
  topLeft: number;
  topRight: number;
  bottomLeft: number;
  bottomRight: number;
}

// Enhanced background color generation that supports gradients
const getEnhancedBackgroundColors = (
  rows: Row<OrderEntryTableRow>[],
  rowSelection: RowSelectionState,
  viewType: AggregatedOrderViewType,
  defaultColor: string = ChakraColor.gray500
): (
  | string
  | ((
      context: ScriptableContext<'bar' | 'doughnut'>
    ) => string | CanvasGradient)
)[] => {
  const isHorizontal =
    viewType === AggregatedOrderViewType.StackedHorizontalBarChart;

  return rows.map((row, index) => {
    const color = row.original.color ?? defaultColor;
    const isSelected = rowSelection[row.id] ?? false;

    if (isGradient(color)) {
      // For gradients, return a function that creates the gradient with appropriate alpha
      return function (
        context: ScriptableContext<'bar' | 'doughnut'>
      ): string | CanvasGradient {
        const dataIndex = context.dataIndex;

        // Make sure we're dealing with the correct row
        if (dataIndex !== index) {
          return 'rgba(0, 0, 0, 0)'; // Transparent for wrong index
        }

        // Use alpha based on selection state (same as solid colors)
        const alpha = isSelected ? 1.0 : 0.3;
        const gradientFunction = createGradientFunction(
          color,
          isHorizontal,
          alpha
        );

        const gradientResult = gradientFunction(context);
        return gradientResult || 'rgba(0, 0, 0, 0)';
      };
    } else {
      // For solid colors, use the existing logic
      const [r, g, b] = hexToRgb(color);
      const alpha = isSelected ? 1.0 : 0.3;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  });
};

// Generate basic chart data (for bar, horizontal-bar, doughnut)
export const generateBasicChartData = (
  table: Table<OrderEntryTableRow>,
  grouping: string[],
  rowSelection: RowSelectionState,
  viewType: AggregatedOrderViewType
): ChartData<'bar'> | ChartData<'doughnut'> => {
  let labelKey = 'title';
  if (grouping.length > 0) {
    labelKey = table.getState().grouping[0];
  }

  const tableRows: Row<OrderEntryTableRow>[] = table
    .getRowModel()
    .rows.filter((row) => !row.parentId);

  const labels = tableRows.map((row) => String(row.getValue(labelKey) ?? ''));
  const data = tableRows.map((row) =>
    round(parseNumericValue(row.getValue('quantity')), 2)
  );

  // Use enhanced background colors that support gradients
  const backgroundColors = getEnhancedBackgroundColors(
    tableRows,
    rowSelection,
    viewType
  ) as string[];
  const borderColors = tableRows.map((row) => {
    const color = row.original.color ?? ChakraColor.gray500;
    // For gradients, use the first color as border, for solid colors use as-is
    if (isGradient(color)) {
      // Extract first color from gradient for border
      const match = color.match(/linear-gradient\([^,]+,\s*([^,]+)/);
      return match ? match[1].trim() : ChakraColor.gray500;
    }
    return color;
  });

  // Create the base dataset structure
  const baseDataset = {
    label:
      viewType === AggregatedOrderViewType.DoughnutChart
        ? undefined
        : 'Quantity',
    data: data,
    backgroundColor: backgroundColors,
    borderColor: borderColors,
    borderWidth: 2,
  };

  let dataset: CustomBarDataset | CustomDoughnutDataset;

  if (viewType === AggregatedOrderViewType.DoughnutChart) {
    dataset = baseDataset as CustomDoughnutDataset;
  } else {
    // For bar charts, add border radius configuration
    const borderRadius = 6;
    const borderRadiusConfig = (() => {
      if (
        viewType === AggregatedOrderViewType.HorizontalBarChart ||
        viewType === AggregatedOrderViewType.StackedHorizontalBarChart
      ) {
        // For horizontal bars, only round the right corners
        return {
          topLeft: 0,
          topRight: borderRadius,
          bottomLeft: 0,
          bottomRight: borderRadius,
        };
      } else {
        // For vertical bars, only round the top corners
        return {
          topLeft: borderRadius,
          topRight: borderRadius,
          bottomLeft: 0,
          bottomRight: 0,
        };
      }
    })();

    dataset = {
      ...baseDataset,
      borderRadius: borderRadiusConfig,
    } as CustomBarDataset;
  }

  return {
    labels,
    datasets: [dataset],
  } as ChartData<'bar'> | ChartData<'doughnut'>;
};

// Generate stacked chart data (for stacked-bar, stacked-horizontal-bar, line)
export const generateStackedChartData = (
  table: Table<OrderEntryTableRow>,
  grouping: string[],
  rowSelection: RowSelectionState,
  viewType: AggregatedOrderViewType
): ChartData<'bar'> | ChartData<'line'> => {
  let firstGroupId = 'title';
  let secondGroupId = null as string | null;

  if (grouping.length === 1) {
    firstGroupId = table.getState().grouping[0];
    secondGroupId = 'title';
  } else if (grouping.length > 1) {
    firstGroupId = table.getState().grouping[0];
    secondGroupId = table.getState().grouping[1];
  }

  const tableRows: Row<OrderEntryTableRow>[] = table
    .getRowModel()
    .rows.filter((row) => !row.parentId);
  const labels = tableRows.map((row) =>
    String(row.getValue(firstGroupId) ?? '')
  );
  const allSubRows = tableRows.flatMap((row) => row.subRows || []);

  // If no sub-rows exist, fall back to basic data
  if (allSubRows.length === 0) {
    // Pass only firstGroupId for basic chart data if second is not applicable
    return generateBasicChartData(
      table,
      [firstGroupId],
      rowSelection,
      viewType
    ) as ChartData<'bar'> | ChartData<'line'>;
  }

  let uniqueStackKeys: string[];
  if (secondGroupId) {
    // Check if secondGroupId is a valid column accessor on subRows
    // This is a basic check; robust validation might involve checking table column definitions
    if (
      allSubRows.length > 0 &&
      secondGroupId &&
      allSubRows[0].getValue(secondGroupId) === undefined &&
      !('name' === secondGroupId && allSubRows[0].original.name !== undefined)
    ) {
      console.warn(
        `generateStackedChartData: secondGroupId "${secondGroupId}" does not seem to be a valid accessor on subRows. Falling back to 'original.name' for stacks.`
      );
      uniqueStackKeys = [
        ...new Set(allSubRows.map((subRow) => subRow.original.name)),
      ];
    } else {
      uniqueStackKeys = [
        ...new Set(
          allSubRows.map((subRow) =>
            secondGroupId
              ? String(subRow.getValue(secondGroupId) ?? '')
              : subRow.original.name
          )
        ),
      ];
    }
  } else {
    uniqueStackKeys = [
      ...new Set(allSubRows.map((subRow) => subRow.original.name)),
    ];
  }

  // Filter out empty or null stack keys, as they don't make sense for dataset labels
  uniqueStackKeys = uniqueStackKeys.filter((key) => key && key.trim() !== '');

  const datasets = uniqueStackKeys.map((stackKeyValue, index) => {
    const data = tableRows.map((parentRow) => {
      const matchingSubRow = (parentRow.subRows || []).find((subRow) => {
        const valueToCompare = secondGroupId
          ? String(subRow.getValue(secondGroupId) ?? '')
          : subRow.original.name;
        return valueToCompare === stackKeyValue;
      });
      return matchingSubRow
        ? round(parseNumericValue(matchingSubRow.getValue('quantity')), 2)
        : 0;
    });

    const color = ChakraColor.gray500; // Fallback, not used for segment colors
    const [r, g, b] = hexToRgb(color);

    const subRowsMetadata = tableRows.map((parentRow) => {
      const matchingSubRow = (parentRow.subRows || []).find((subRow) => {
        const valueToCompare = secondGroupId
          ? String(subRow.getValue(secondGroupId) ?? '')
          : subRow.original.name;
        return valueToCompare === stackKeyValue;
      });
      return matchingSubRow || null;
    });

    if (viewType === AggregatedOrderViewType.LineChart) {
      return createLineDataset(
        stackKeyValue, // This is now the value from grouping[1] or name
        data,
        color,
        r,
        g,
        b,
        tableRows,
        rowSelection,
        subRowsMetadata,
        secondGroupId // Pass secondGroupId to createLineDataset
      );
    } else {
      return createStackedBarDataset(
        stackKeyValue, // This is now the value from grouping[1] or name
        data,
        color,
        r,
        g,
        b,
        tableRows,
        rowSelection,
        subRowsMetadata,
        index,
        uniqueStackKeys,
        viewType,
        secondGroupId // Pass secondGroupId to createStackedBarDataset
      );
    }
  });

  return { labels, datasets } as ChartData<'bar'> | ChartData<'line'>;
};

// Create line dataset
const createLineDataset = (
  datasetLabel: string,
  data: number[],
  color: string,
  r: number,
  g: number,
  b: number,
  tableRows: Row<OrderEntryTableRow>[],
  rowSelection: RowSelectionState,
  subRowsMetadata: (Row<OrderEntryTableRow> | null)[],
  secondGroupId: string | null
): CustomLineDataset => {
  const borderColor = function (context: ScriptableContext<'line'>) {
    const dataIndex = context.dataIndex;
    const parentRow = tableRows[dataIndex];
    if (!parentRow) return `rgba(${r}, ${g}, ${b}, 0.3)`;

    const matchingSubRow = (parentRow.subRows || []).find((subRow) => {
      const valueToCompare = secondGroupId
        ? String(subRow.getValue(secondGroupId) ?? '')
        : subRow.original.name;
      return valueToCompare === datasetLabel;
    });

    if (!matchingSubRow) return `rgba(${r}, ${g}, ${b}, 0)`;
    const actualColor = matchingSubRow.original.color ?? color;
    const isSelected = rowSelection[matchingSubRow.id] ?? false;
    const lineAlpha = isSelected ? 1.0 : 0.3;

    if (isGradient(actualColor)) {
      const gradientFunction = createGradientFunction(
        actualColor,
        false,
        lineAlpha
      );
      return (
        gradientFunction(context) || `rgba(${r}, ${g}, ${b}, ${lineAlpha})`
      );
    } else {
      const [ar, ag, ab] = hexToRgb(actualColor);
      return `rgba(${ar}, ${ag}, ${ab}, ${lineAlpha})`;
    }
  };

  const backgroundColor = function (context: ScriptableContext<'line'>) {
    const dataIndex = context.dataIndex;
    const parentRow = tableRows[dataIndex];
    if (!parentRow) return `rgba(${r}, ${g}, ${b}, 0.1)`;

    const matchingSubRow = (parentRow.subRows || []).find((subRow) => {
      const valueToCompare = secondGroupId
        ? String(subRow.getValue(secondGroupId) ?? '')
        : subRow.original.name;
      return valueToCompare === datasetLabel;
    });

    if (!matchingSubRow) return `rgba(${r}, ${g}, ${b}, 0)`;
    const actualColor = matchingSubRow.original.color ?? color;

    if (isGradient(actualColor)) {
      const gradientFunction = createGradientFunction(actualColor, false, 0.1);
      return gradientFunction(context) || `rgba(${r}, ${g}, ${b}, 0.1)`;
    } else {
      const [ar, ag, ab] = hexToRgb(actualColor);
      return `rgba(${ar}, ${ag}, ${ab}, 0.1)`;
    }
  };

  const getPointColor = (
    subRow: Row<OrderEntryTableRow> | null,
    isSelected: boolean
  ) => {
    if (!subRow) return `rgba(${r}, ${g}, ${b}, 0)`; // Default transparent if no subRow

    const pointColorSource = subRow.original.color ?? ChakraColor.gray500;
    const alpha = isSelected ? 1.0 : 0.3;

    if (isGradient(pointColorSource)) {
      const match = pointColorSource.match(/linear-gradient\([^,]+,\s*([^,]+)/);
      const gradientStartColor = match ? match[1].trim() : pointColorSource;
      const [pr, pg, pb] = hexToRgb(gradientStartColor);
      return `rgba(${pr}, ${pg}, ${pb}, ${alpha})`;
    } else {
      const [pr, pg, pb] = hexToRgb(pointColorSource);
      return `rgba(${pr}, ${pg}, ${pb}, ${alpha})`;
    }
  };

  return {
    label: datasetLabel,
    data: data,
    borderColor: borderColor,
    backgroundColor: backgroundColor,
    borderWidth: 2,
    fill: false,
    tension: 0.1,
    pointBackgroundColor: tableRows.map((parentRow) => {
      const matchingSubRow = (parentRow.subRows || []).find((subRow) => {
        const valueToCompare = secondGroupId
          ? String(subRow.getValue(secondGroupId) ?? '')
          : subRow.original.name;
        return valueToCompare === datasetLabel;
      });
      const isSelected = matchingSubRow
        ? rowSelection[matchingSubRow.id] ?? false
        : false;
      return getPointColor(matchingSubRow || null, isSelected);
    }),
    pointBorderColor: ((): string => {
      const firstValidSubRow = tableRows
        .map((parentRow) => {
          const matchingSubRow = (parentRow.subRows || []).find((subRow) => {
            const valueToCompare = secondGroupId
              ? String(subRow.getValue(secondGroupId) ?? '')
              : subRow.original.name;
            return valueToCompare === datasetLabel;
          });
          return matchingSubRow;
        })
        .find((sr) => sr);

      const seriesColor =
        firstValidSubRow?.original?.color ?? ChakraColor.gray500;
      if (isGradient(seriesColor)) {
        const match = seriesColor.match(/linear-gradient\([^,]+,\s*([^,]+)/);
        return match ? match[1].trim() : seriesColor;
      }
      return seriesColor;
    })(),
    pointRadius: 5,
    pointHoverRadius: 7,
    meta: {
      subRows: subRowsMetadata,
    },
  };
};

// Create stacked bar dataset
const createStackedBarDataset = (
  datasetLabel: string,
  data: number[],
  color: string,
  r: number,
  g: number,
  b: number,
  tableRows: Row<OrderEntryTableRow>[],
  rowSelection: RowSelectionState,
  subRowsMetadata: (Row<OrderEntryTableRow> | null)[],
  index: number,
  allDatasetLabels: string[],
  viewType: AggregatedOrderViewType,
  secondGroupId: string | null
): CustomBarDataset => {
  const isHorizontal =
    viewType === AggregatedOrderViewType.StackedHorizontalBarChart;

  const backgroundColor = function (context: ScriptableContext<'bar'>) {
    const dataIndex = context.dataIndex;
    const parentRow = tableRows[dataIndex];

    if (!parentRow) return 'rgba(0, 0, 0, 0)';

    const matchingSubRow = (parentRow.subRows || []).find((subRow) => {
      const valueToCompare = secondGroupId
        ? String(subRow.getValue(secondGroupId) ?? '')
        : subRow.original.name;
      return valueToCompare === datasetLabel;
    });

    if (!matchingSubRow) {
      return 'rgba(0, 0, 0, 0)';
    }

    const actualColor = matchingSubRow.original.color ?? ChakraColor.gray500;
    const isSelected = rowSelection[matchingSubRow.id] ?? false;
    const alpha = isSelected ? 0.9 : 0.3;

    if (isGradient(actualColor)) {
      const gradientFunction = createGradientFunction(
        actualColor,
        isHorizontal,
        alpha
      );
      return gradientFunction(context) || 'rgba(0,0,0,0)';
    } else {
      const [ar, ag, ab] = hexToRgb(actualColor);
      return `rgba(${ar}, ${ag}, ${ab}, ${alpha})`;
    }
  };

  const borderRadius = function (
    context: ScriptableContext<'bar'>
  ): number | BorderRadiusConfig {
    const dataIndex = context.dataIndex;
    const parentRow = tableRows[dataIndex];

    if (!parentRow) return 0;

    const datasetsWithDataForThisBar = allDatasetLabels.filter((label) => {
      const matchingSubRowForLabel = (parentRow.subRows || []).find(
        (subRow) => {
          const valueToCompare = secondGroupId
            ? String(subRow.getValue(secondGroupId) ?? '')
            : subRow.original.name;
          return valueToCompare === label;
        }
      );
      const quantity = matchingSubRowForLabel
        ? parseNumericValue(matchingSubRowForLabel.getValue('quantity'))
        : 0;
      return quantity > 0;
    });

    const isTopmostForThisBar =
      datasetsWithDataForThisBar.length > 0 &&
      datasetsWithDataForThisBar[datasetsWithDataForThisBar.length - 1] ===
        datasetLabel;

    const isOnlyDatasetForThisBar = datasetsWithDataForThisBar.length === 1;

    if (isOnlyDatasetForThisBar || isTopmostForThisBar) {
      return isHorizontal
        ? { topLeft: 0, bottomLeft: 0, topRight: 4, bottomRight: 4 }
        : { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 };
    }
    return 0;
  };

  return {
    label: datasetLabel,
    data: data,
    backgroundColor: backgroundColor,
    borderColor: function (context: ScriptableContext<'bar'>) {
      const dataIndex = context.dataIndex;
      const parentRow = tableRows[dataIndex];
      if (!parentRow) return ChakraColor.gray500;

      const matchingSubRow = (parentRow.subRows || []).find((subRow) => {
        const valueToCompare = secondGroupId
          ? String(subRow.getValue(secondGroupId) ?? '')
          : subRow.original.name;
        return valueToCompare === datasetLabel;
      });

      if (!matchingSubRow) {
        return ChakraColor.gray500;
      }

      const actualColor = matchingSubRow.original.color ?? ChakraColor.gray500;

      if (isGradient(actualColor)) {
        const match = actualColor.match(/linear-gradient\([^,]+,\s*([^,]+)/);
        return match ? match[1].trim() : ChakraColor.gray500;
      } else {
        return actualColor;
      }
    },
    borderWidth: 1,
    borderRadius: borderRadius,
    borderSkipped: false,
    stack: 'stack1', // All datasets belong to the same stack group
    meta: {
      subRows: subRowsMetadata, // This metadata still refers to the innermost subRows
    },
  };
};
